// WP-C2.3 (CW-1..3, SB-1): create/edit an event on a write-capable calendar connection — the
// same FormSheet modal convention every other form in this app uses (TransactionForm.tsx).
// CW-1's field set, and NO MORE: title, date/time + zone, duration, target calendar, optional
// note — deliberately no attendees/location/video-link (CW-3), no "all-day" toggle (not in
// CW-1's field list). Editing reuses this exact same form pre-filled; Delete lives inside it,
// gated by ConfirmDialog's requireTypedConfirmation on the event's own title (CW-2/SB-1).
import type { CalendarEventResponse } from '@pfos/shared';
import { useEffect, useState } from 'react';
import { ConfirmDialog } from './ConfirmDialog';
import { Field, inputClass, selectClass } from './FormKit';
import { FormSheet } from './FormSheet';
import {
  useCalendars,
  useCreateCalendarEvent,
  useDeleteCalendarEvent,
  usePreviewCalendarEvent,
  useUpdateCalendarEvent,
} from '../lib/calendars';
import { ApiError } from '../lib/api';
import {
  browserTimeZone,
  isValidTimeZone,
  listTimeZones,
  utcIsoToZonedDateAndTime,
  zonedDateTimeToUtcIso,
} from '../lib/localDateTime';

/** What Agenda.tsx has on hand for an already-existing event (AgendaEventResponse's own shape,
 * trimmed to what this form needs) — passed in to switch the form into edit mode, pre-filled. */
export interface CalendarEventFormEditingEvent {
  calendarConnectionId: string;
  calendarLabel: string;
  uid: string;
  title: string;
  startAt: string;
  endAt: string;
}

const DEFAULT_DURATION_MINUTES = '30';
const NONE_SELECTED = 'none-selected'; // a syntactically-valid but never-real connection id, so
// the preview/create/update/delete hooks below always have SOME string to build a URL from,
// even before the user has picked a target calendar (the resulting request is simply never sent).

function minutesBetween(startIso: string, endIso: string): number {
  return Math.max(
    5,
    Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60_000),
  );
}

export function CalendarEventForm({
  open,
  onOpenChange,
  editing,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absent -> create mode. Present -> edit mode, pre-filled, with a Delete action. */
  editing?: CalendarEventFormEditingEvent;
}): React.JSX.Element {
  const { data: calendars } = useCalendars();

  const [title, setTitle] = useState('');
  const [calendarId, setCalendarId] = useState('');
  const [dateStr, setDateStr] = useState('');
  const [timeStr, setTimeStr] = useState('');
  const [timeZone, setTimeZone] = useState('UTC');
  const [duration, setDuration] = useState(DEFAULT_DURATION_MINUTES);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [conflicts, setConflicts] = useState<CalendarEventResponse[]>([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setDeleteError(null);
    setDeleteOpen(false);
    setConflicts([]);
    const zone = browserTimeZone();
    if (editing !== undefined) {
      const { date, time } = utcIsoToZonedDateAndTime(editing.startAt, zone);
      setTitle(editing.title);
      setCalendarId(editing.calendarConnectionId);
      setDateStr(date);
      setTimeStr(time);
      setTimeZone(zone);
      setDuration(String(minutesBetween(editing.startAt, editing.endAt)));
      setNote(''); // the write-side event shape doesn't carry note back (see calendarEventSchema)
    } else {
      setTitle('');
      setCalendarId('');
      setDateStr('');
      setTimeStr('');
      setTimeZone(zone);
      setDuration(DEFAULT_DURATION_MINUTES);
      setNote('');
    }
    // Re-runs only on genuine open/target-event changes, not on every parent re-render (this
    // repo has no react-hooks lint plugin to police the dep list, so this is a deliberate,
    // documented choice rather than a suppressed warning).
  }, [
    open,
    editing?.uid,
    editing?.calendarConnectionId,
    editing?.title,
    editing?.startAt,
    editing?.endAt,
  ]);

  const targetConnectionId = editing !== undefined ? editing.calendarConnectionId : calendarId;
  const selectedCalendar = (calendars ?? []).find((c) => c.id === targetConnectionId);
  const zoneValid = isValidTimeZone(timeZone);
  const startAt = zoneValid ? zonedDateTimeToUtcIso(dateStr, timeStr, timeZone) : null;
  const durationMinutes = Number(duration);
  const endAt =
    startAt !== null && Number.isFinite(durationMinutes) && durationMinutes > 0
      ? new Date(new Date(startAt).getTime() + durationMinutes * 60_000).toISOString()
      : null;

  const previewMutation = usePreviewCalendarEvent(targetConnectionId || NONE_SELECTED);
  const createMutation = useCreateCalendarEvent(calendarId || NONE_SELECTED);
  const updateMutation = useUpdateCalendarEvent(
    editing?.calendarConnectionId ?? NONE_SELECTED,
    editing?.uid ?? NONE_SELECTED,
  );
  const deleteMutation = useDeleteCalendarEvent(
    editing?.calendarConnectionId ?? NONE_SELECTED,
    editing?.uid ?? NONE_SELECTED,
  );

  // CW-1: "conflicts are warned before confirm" — re-check on every meaningful change to the
  // computed instants (date/time/duration/zone all fold into startAt/endAt; changing the
  // target calendar changes which cache is checked). A conflict only WARNS; it never blocks
  // the Confirm button below.
  useEffect(() => {
    if (
      targetConnectionId === '' ||
      selectedCalendar?.provider === 'ics' ||
      startAt === null ||
      endAt === null
    ) {
      setConflicts([]);
      return;
    }
    previewMutation.mutate(
      { title: title.trim() !== '' ? title.trim() : 'Untitled event', startAt, endAt, timeZone },
      {
        onSuccess: (data) => {
          // The foundation agent's previewCreate doesn't exclude the event being edited from
          // its own conflict scan (it has no excludeEventId parameter) — an edit that doesn't
          // move the event would otherwise "conflict" with itself. Filtered out client-side.
          setConflicts(data.conflicts.filter((c) => c.uid !== editing?.uid));
        },
        onError: () => {
          setConflicts([]);
        },
      },
    );
    // Deliberately NOT depending on previewMutation.mutate: usePreviewCalendarEvent is called
    // fresh every render (its connectionId argument can change), so its `mutate` identity is
    // new every render too — depending on it here would refire this effect every render (no
    // react-hooks lint plugin in this repo to flag the omission either way).
  }, [targetConnectionId, startAt, endAt, timeZone]);

  const handleSubmit = async (): Promise<void> => {
    setError(null);
    if (title.trim() === '') {
      setError('Enter a title');
      return;
    }
    if (editing === undefined && calendarId === '') {
      setError('Choose a calendar');
      return;
    }
    if (editing === undefined && selectedCalendar?.provider === 'ics') {
      setError('That calendar is read-only — choose a write-capable one');
      return;
    }
    if (!zoneValid) {
      setError('Enter a valid time zone');
      return;
    }
    if (startAt === null) {
      setError('Enter a valid date and time');
      return;
    }
    if (endAt === null) {
      setError('Enter a valid duration');
      return;
    }

    const payload = {
      title: title.trim(),
      startAt,
      endAt,
      timeZone,
      ...(note.trim() !== '' ? { note: note.trim() } : {}),
    };
    try {
      if (editing === undefined) {
        await createMutation.mutateAsync(payload);
      } else {
        await updateMutation.mutateAsync(payload);
      }
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save the event');
    }
  };

  return (
    <>
      <FormSheet
        open={open}
        onOpenChange={onOpenChange}
        title={editing === undefined ? 'New event' : 'Edit event'}
        submitLabel={editing === undefined ? 'Create' : 'Save'}
        submitting={createMutation.isPending || updateMutation.isPending}
        onSubmit={() => {
          void handleSubmit();
        }}
      >
        <Field label="Title" htmlFor="cal-event-title">
          <input
            id="cal-event-title"
            className={inputClass}
            value={title}
            autoFocus
            onChange={(e) => {
              setTitle(e.target.value);
            }}
          />
        </Field>

        {editing === undefined ? (
          <Field label="Calendar" htmlFor="cal-event-calendar">
            <select
              id="cal-event-calendar"
              className={selectClass}
              value={calendarId}
              onChange={(e) => {
                setCalendarId(e.target.value);
              }}
            >
              <option value="">Select a calendar…</option>
              {(calendars ?? []).map((c) => (
                <option key={c.id} value={c.id} disabled={c.provider === 'ics'}>
                  {c.label}
                  {c.provider === 'ics' ? ' (read-only — ICS feed)' : ''}
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <p className="mb-3 text-sm text-muted">
            Calendar: <span className="text-ink">{editing.calendarLabel}</span>
          </p>
        )}

        <div className="flex gap-2">
          <div className="flex-1">
            <Field label="Date" htmlFor="cal-event-date">
              <input
                id="cal-event-date"
                type="date"
                className={inputClass}
                value={dateStr}
                onChange={(e) => {
                  setDateStr(e.target.value);
                }}
              />
            </Field>
          </div>
          <div className="flex-1">
            <Field label="Time" htmlFor="cal-event-time">
              <input
                id="cal-event-time"
                type="time"
                className={inputClass}
                value={timeStr}
                onChange={(e) => {
                  setTimeStr(e.target.value);
                }}
              />
            </Field>
          </div>
        </div>

        <div className="flex gap-2">
          <div className="flex-1">
            <Field label="Time zone" htmlFor="cal-event-timezone">
              <input
                id="cal-event-timezone"
                className={inputClass}
                list="cal-event-timezone-options"
                value={timeZone}
                onChange={(e) => {
                  setTimeZone(e.target.value);
                }}
              />
              <datalist id="cal-event-timezone-options">
                {listTimeZones().map((tz) => (
                  <option key={tz} value={tz} />
                ))}
              </datalist>
            </Field>
          </div>
          <div className="w-28">
            <Field label="Duration (min)" htmlFor="cal-event-duration">
              <input
                id="cal-event-duration"
                type="number"
                min={5}
                step={5}
                className={inputClass}
                value={duration}
                onChange={(e) => {
                  setDuration(e.target.value);
                }}
              />
            </Field>
          </div>
        </div>

        <Field label="Note (optional)" htmlFor="cal-event-note">
          <input
            id="cal-event-note"
            className={inputClass}
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
            }}
          />
        </Field>

        {conflicts.length > 0 && (
          <div className="mb-3 rounded-[var(--radius-control)] border border-warning/30 bg-warning/10 p-3 text-sm">
            <p className="mb-1 font-medium text-warning">
              {conflicts.length} possible conflict{conflicts.length === 1 ? '' : 's'} — you can
              still create this event
            </p>
            <ul className="space-y-0.5 text-muted">
              {conflicts.map((c) => (
                <li key={c.id}>
                  {c.title} — {new Date(c.startAt).toLocaleString()}
                </li>
              ))}
            </ul>
          </div>
        )}

        {editing !== undefined && (
          <button
            type="button"
            className="mb-1 text-sm text-danger hover:underline"
            onClick={() => {
              setDeleteOpen(true);
            }}
          >
            Delete event
          </button>
        )}

        {error !== null && <p className="text-sm text-danger">{error}</p>}
        {deleteError !== null && <p className="text-sm text-danger">{deleteError}</p>}
      </FormSheet>

      {editing !== undefined && (
        <ConfirmDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title="Delete event"
          consequence={`This permanently deletes "${editing.title}" from the provider calendar. This cannot be undone.`}
          confirmLabel="Delete"
          requireTypedConfirmation={editing.title}
          confirming={deleteMutation.isPending}
          onConfirm={() => {
            setDeleteError(null);
            deleteMutation.mutate(editing.title, {
              onSuccess: () => {
                setDeleteOpen(false);
                onOpenChange(false);
              },
              onError: (e) => {
                setDeleteError(e instanceof ApiError ? e.message : 'Could not delete the event');
              },
            });
          }}
        />
      )}
    </>
  );
}
